export interface AdjustmentOptions {
  /** 亮度調整，範圍建議在 -1 ~ 1 之間，0 表示不調整 */
  readonly brightness?: number;
  /** 對比調整，範圍建議在 -1 ~ 1 之間，0 表示不調整 */
  readonly contrast?: number;
  /** 飽和度調整，範圍建議在 -1 ~ 1 之間，0 表示不調整 */
  readonly saturation?: number;
}

function clamp(value: number, min: number, max: number): number {
  if (value < min) return min;
  if (value > max) return max;
  return value;
}

function applyBrightness(value: number, amount: number): number {
  return value + 255 * amount;
}

function applyContrast(value: number, amount: number): number {
  const factor = (259 * (amount + 1)) / (255 * (1 - amount));
  return factor * (value - 128) + 128;
}

function applySaturation(
  r: number,
  g: number,
  b: number,
  amount: number
): [number, number, number] {
  // 轉 HSL 前的近似灰階值，使用簡化權重即可達到實務上足夠的效果
  const gray = 0.3 * r + 0.59 * g + 0.11 * b;
  const mix = (channel: number): number =>
    gray + (channel - gray) * (amount + 1);
  return [mix(r), mix(g), mix(b)];
}

/**
 * 對 Canvas 內容套用基本調整（亮度／對比／飽和度）。
 *
 * 此函式會「就地」修改傳入的 Canvas，不會建立新的 Canvas，
 * 適合在互動調整時直接更新預覽畫面。
 */
export function applyAdjustmentsToCanvas(
  canvas: HTMLCanvasElement,
  options: AdjustmentOptions
): void {
  const context = canvas.getContext('2d');
  if (!context) {
    throw new Error('無法取得畫布內容');
  }

  const imageData = context.getImageData(0, 0, canvas.width, canvas.height);
  const { data } = imageData;

  const brightness = clamp(options.brightness ?? 0, -1, 1);
  const contrast = clamp(options.contrast ?? 0, -1, 1);
  const saturation = clamp(options.saturation ?? 0, -1, 1);

  const needBrightness = brightness !== 0;
  const needContrast = contrast !== 0;
  const needSaturation = saturation !== 0;

  if (!needBrightness && !needContrast && !needSaturation) {
    return;
  }

  for (let index = 0; index < data.length; index += 4) {
    let r = data[index];
    let g = data[index + 1];
    let b = data[index + 2];

    if (needBrightness) {
      r = applyBrightness(r, brightness);
      g = applyBrightness(g, brightness);
      b = applyBrightness(b, brightness);
    }

    if (needContrast) {
      r = applyContrast(r, contrast);
      g = applyContrast(g, contrast);
      b = applyContrast(b, contrast);
    }

    if (needSaturation) {
      [r, g, b] = applySaturation(r, g, b, saturation);
    }

    data[index] = clamp(Math.round(r), 0, 255);
    data[index + 1] = clamp(Math.round(g), 0, 255);
    data[index + 2] = clamp(Math.round(b), 0, 255);
  }

  context.putImageData(imageData, 0, 0);
}
